import { useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useMemo, useState, useRef } from 'react'
import { useAuthStore, useStudioStore } from '@/store'
import { Avatar, Badge, FeedbackButton, ThemeToggle } from '@/components/ui'
import { HeaderStudioSelector, GlobalAnalyticsSelector } from '@/components'
import { OriginalCaseNotificationsBell } from '@/components/OriginalCaseNotificationsBell'
import { getInitials } from '@/lib/utils'
import { softRefresh, hardReset } from '@/lib/appRefresh'
import { confirmLeaveUnsavedChanges } from '@/lib/unsavedChanges'
import { getPedagogySectionTitle } from '@/lib/pedagogyRoutes'
import { Home, RotateCcw, LogOut, ChevronDown, Building2, MapPin, Settings, Search } from 'lucide-react'

export function Header() {
  const { user } = useAuthStore()
  const {
    selectedStudio,
    studios,
    partners,
    selectedPartnerNumber,
    getPartnerStudios
  } = useStudioStore()
  const location = useLocation()
  const navigate = useNavigate()
  const isStudioContext =
    /^\/partners\/[^/]+\/studios\/[^/]+\/.+/.test(location.pathname) ||
    location.pathname.startsWith('/studio/')
  const isManagementCompany = studios.some(
    (studio) => studio.access_kind === 'management_company' || studio.role === 'management_company'
  )
  const isSeniorTeacherContext = selectedStudio?.access_kind === 'senior_teacher' || selectedStudio?.role === 'senior_teacher'
  const isTeacherContext = selectedStudio?.role === 'teacher' && !isSeniorTeacherContext
  const isPartnerTeachersRoute = /^\/partners\/[^/]+\/studios\/[^/]+\/teachers(?:\/.*)?$/.test(location.pathname)

  const legacyPlanRouteMatch = location.pathname.match(/^\/(studio-metrics|studio-metrics-grid|plan-history)\/(\d+)/)
  const partnerPlanRouteMatch = location.pathname.match(
    /^\/partners\/[^/]+\/studios\/(\d+)\/(studio-metrics|studio-metrics-grid|plan-history)$/
  )
  const planRouteType = partnerPlanRouteMatch?.[2] ?? legacyPlanRouteMatch?.[1] ?? null
  const matchedStudioId = partnerPlanRouteMatch
    ? Number(partnerPlanRouteMatch[1])
    : legacyPlanRouteMatch
      ? Number(legacyPlanRouteMatch[2])
      : null
  const matchedStudio = matchedStudioId
    ? studios.find(studio => studio.studio_internal_id === matchedStudioId)
    : null
  const matchedStudioLabel = matchedStudio
    ? `Студия №${matchedStudio.studio_number}${matchedStudio.address ? `, ${matchedStudio.address}` : ''}`
    : 'Студия'

  // Состояние для скрытой кнопки очистки кэша
  const [showClearCache, setShowClearCache] = useState(false)
  const [isRefreshingApp, setIsRefreshingApp] = useState(false)
  const [isPartnerDropdownOpen, setIsPartnerDropdownOpen] = useState(false)
  const [isTeacherCityOpen, setIsTeacherCityOpen] = useState(false)
  const [isRatingsContextOpen, setIsRatingsContextOpen] = useState(false)
  const [partnerSearch, setPartnerSearch] = useState('')
  const clickCountRef = useRef(0)
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const partnerDropdownRef = useRef<HTMLDivElement>(null)

  const getPageTitle = () => {
    if (location.pathname === '/') {
      return 'Мои студии'
    }

    if (location.pathname === '/analytics') {
      return 'Аналитика'
    }

    if (location.pathname === '/partners') {
      return 'Франчайзи'
    }

    if (/^\/partners\/[^/]+\/analytics$/.test(location.pathname)) {
      return 'Сводная аналитика'
    }

    if (/^\/partners\/[^/]+\/marketing(\/.*)?$/.test(location.pathname)) {
      return 'Маркетинг'
    }

    if (/^\/partners\/[^/]+\/cities\/\d+\/analytics$/.test(location.pathname)) {
      return 'Сводная аналитика'
    }

    if (/^\/partners\/[^/]+\/studios$/.test(location.pathname)) {
      return 'Студии франчайзи'
    }

    if (location.pathname === '/management' || /^\/partners\/[^/]+\/management$/.test(location.pathname)) {
      return 'Управление'
    }

    if (location.pathname === '/settings/notifications') {
      return 'Уведомления'
    }

    if (location.pathname === '/admin/notifications') {
      return 'Управление уведомлениями'
    }

    if (location.pathname === '/management/original' || location.pathname === '/management/original/cases') {
      return location.pathname === '/management/original' ? 'Реестр студий и франчайзи' : 'Кейсы'
    }

    if (location.pathname === '/management-dashboard') {
      return 'Дашборд УК'
    }

    if (location.pathname === '/management/network-planning' || location.pathname === '/management/network-planning/edit') {
      return location.pathname.endsWith('/edit') ? 'Годовой план сети' : 'Экран планирования сети'
    }

    if (location.pathname === '/management-analytics') {
      return 'Аналитика УК'
    }

    if (location.pathname === '/management-ratings') {
      return 'Единый рейтинг сети'
    }
    if (location.pathname === '/management-ratings/subscriptions') {
      return 'Рейтинги по подпискам'
    }

    if (location.pathname === '/management/territorial-managers') {
      return 'Тер. управляющие'
    }
    if (/^\/management\/territorial-managers\/[^/]+$/.test(location.pathname)) {
      return 'Тер. управляющий'
    }
    if (location.pathname === '/management/personal-managers') {
      return 'Личные менеджеры'
    }
    if (location.pathname === '/management/personal-managers-hackathon') {
      return 'Хакатон ЛМ'
    }
    if (location.pathname === '/management/personal-managers-coverage') {
      return 'Назначения ЛМ'
    }
    if (location.pathname === '/management/personal-managers-control') {
      return 'Контроль ЛМ'
    }
    if (/^\/management\/personal-managers\/[^/]+$/.test(location.pathname)) {
      return 'Личный менеджер'
    }
    // Подписи экранов педагогического раздела — из таблицы маршрутов, а не руками:
    // при ручном перечислении четыре адреса раздела остались без заголовка
    // и показывались как «Partners Results».
    const pedagogyTitle = getPedagogySectionTitle(location.pathname)
    if (pedagogyTitle) {
      return pedagogyTitle
    }
    if (location.pathname === '/management/partners-coverage') {
      return 'Сопровождение франчайзи'
    }

    if (location.pathname === '/royalties/all') {
      return 'Все франчайзи и студии'
    }

    if (location.pathname === '/royalties') {
      return 'Роялти'
    }
    if (/^\/royalties\/partner\/[^/]+$/.test(location.pathname)) {
      return 'Роялти франчайзи'
    }
    if (/^\/royalties\/series\/[^/]+$/.test(location.pathname)) {
      return 'Роялти студии'
    }
    if (/^\/royalties\/contract\/[^/]+$/.test(location.pathname)) {
      return 'Договор'
    }
    if (location.pathname === '/royalties/process') {
      return 'Выставление роялти'
    }
    if (location.pathname === '/royalties/import') {
      return 'Импорт платежей'
    }
    if (location.pathname === '/royalties/allocation') {
      return 'Разбор платежей'
    }
    if (/^\/royalties\/allocation\/[^/]+$/.test(location.pathname)) {
      return 'Разбор платежей'
    }
    if (location.pathname === '/royalties/refunds') {
      return 'Возвраты роялти'
    }
    if (location.pathname === '/royalties/coverage-timeline') {
      return 'Покрытие договорного учета'
    }
    if (location.pathname === '/royalties/fx-exceptions') {
      return 'Валютные исключения'
    }

    if (planRouteType) {
      if (planRouteType === 'studio-metrics') {
        return 'Декомпозиция периода'
      }
      if (planRouteType === 'studio-metrics-grid') {
        return 'Планирование по неделям'
      }
      if (planRouteType === 'plan-history') {
        return 'История планирования'
      }
    }

    if (selectedStudio) {
      if (location.pathname.includes('/metrics')) {
        const isMobileViewport = typeof window !== 'undefined' && window.innerWidth <= 640
        return isMobileViewport ? 'Показатели' : 'Ежедневные показатели'
      }
      if (location.pathname.includes('/history')) {
        return 'План-факт'
      }
      if (location.pathname.includes('/finance')) {
        return 'ДДС / PnL'
      }
      if (/^\/partners\/[^/]+\/studios\/[^/]+\/analytics$/.test(location.pathname)) {
        return 'Аналитика студии'
      }
      if (/^\/partners\/[^/]+\/studios\/[^/]+\/business-health$/.test(location.pathname)) {
        return 'Здоровье бизнеса'
      }
      if (/^\/partners\/[^/]+\/business-health$/.test(location.pathname)) {
        return 'Здоровье бизнеса'
      }
      if (/^\/partners\/[^/]+\/studios\/[^/]+\/clients$/.test(location.pathname)) {
        return 'Клиенты'
      }
      if (location.pathname.includes('/analytics')) {
        return 'Аналитика'
      }
      if (location.pathname.includes('/ratings')) {
        return 'Рейтинги'
      }
      if (/^\/partners\/[^/]+\/studios\/[^/]+\/teachers\/[^/]+\/reviews\/[^/]+$/.test(location.pathname)) {
        return 'Отсмотр'
      }
      if (/^\/partners\/[^/]+\/studios\/[^/]+\/teachers\/[^/]+$/.test(location.pathname)) {
        return 'Карточка педагога'
      }
      if (location.pathname.includes('/teachers') || location.pathname.includes('/trainer-reviews')) {
        return 'Педагоги'
      }
      if (location.pathname.startsWith('/management/original')) {
        return 'Реестр студий и франчайзи'
      }
      if (location.pathname.includes('/management')) {
        return 'Управление'
      }
      return selectedStudio.studio_number
    }

    return 'Partners Results'
  }

  const getPageSubtitle = () => {
    if (/^\/partners\/[^/]+\/marketing\/daily$/.test(location.pathname)) {
      return 'Дневная сводка по таргетологам из Click.'
    }

    if (/^\/partners\/[^/]+\/marketing\/plan$/.test(location.pathname)) {
      return 'Недельное планирование по таргетологам.'
    }

    if (/^\/partners\/[^/]+\/marketing$/.test(location.pathname)) {
      return 'Данные из рекламных кабинетов Click.'
    }

    return null
  }

  const getUserRole = () => {
    if (!user || !studios || studios.length === 0) return null

    // Проверяем, является ли пользователь управляющей компанией
    const isManagementCompany = studios.some(studio => studio.access_kind === 'management_company')
    if (isManagementCompany) {
      return 'management_company'
    }

    // Если мы на глобальных страницах или на странице студий, показываем общую роль
    if (location.pathname === '/' ||
        location.pathname === '/analytics' ||
        location.pathname === '/partners' ||
        /^\/partners\/[^/]+\/analytics$/.test(location.pathname) ||
        /^\/partners\/[^/]+\/cities\/\d+\/analytics$/.test(location.pathname) ||
        /^\/partners\/[^/]+\/management$/.test(location.pathname) ||
        /^\/partners\/[^/]+\/studios$/.test(location.pathname) ||
        location.pathname === '/management' ||
        location.pathname === '/settings/notifications' ||
        location.pathname === '/admin/notifications' ||
        location.pathname === '/management-dashboard' ||
        location.pathname === '/management-analytics' ||
        location.pathname.startsWith('/management-ratings')) {
      // Определяем наивысшую роль пользователя
      const roles = studios.map(studio => studio.role).filter(Boolean)
      if (roles.includes('owner')) return 'owner'
      if (roles.includes('manager')) return 'manager'
      if (roles.includes('teacher')) return 'teacher'
      if (roles.includes('member')) return 'member'
      if (roles.includes('senior_teacher')) return 'senior_teacher'
      return null
    }

    // Если мы на странице конкретной студии, показываем роль в этой студии
    if (selectedStudio) {
      return selectedStudio.role
    }

    return null
  }

  const getRoleBadge = (role: string) => {
    const roleLabels = {
      owner: 'Владелец',
      manager: 'Управляющий',
      member: 'Сотрудник',
      teacher: 'Педагог',
      management_company: 'УК',
      senior_teacher: 'Старший педагог'
    }

    const roleTypes = {
      owner: 'success' as const,
      manager: 'warning' as const,
      member: 'neutral' as const,
      management_company: 'secondary' as const,
      senior_teacher: 'success' as const
    }

    return (
      <Badge
        text={roleLabels[role as keyof typeof roleLabels] || role}
        variant={roleTypes[role as keyof typeof roleTypes] || 'neutral'}
      />
    )
  }

  // Определяем, нужно ли показывать кнопку "На главную"
  const shouldShowHomeButton = () => {
    // Показываем кнопку на страницах студий (глубокий уровень)
    return location.pathname.startsWith('/partners/') ||
           location.pathname === '/analytics' ||
           location.pathname === '/management' ||
           location.pathname === '/settings/notifications' ||
           location.pathname === '/admin/notifications' ||
           location.pathname === '/management/original/cases' ||
           location.pathname === '/management-dashboard' ||
           location.pathname === '/management-analytics' ||
           location.pathname.startsWith('/management-ratings')
  }

  const handleHomeClick = () => {
    if (!confirmLeaveUnsavedChanges()) return
    if (isManagementCompany) {
      navigate('/partners')
      return
    }

    if (currentPartner?.partnerNumber) {
      // Member/Manager: на первую студию /metrics
      const roles = studios.map(s => s.role).filter(Boolean)
      const isOwner = roles.includes('owner')
      if (!isOwner) {
        const partnerStudios = studios.filter(
          s => Number(s.partner_number) === currentPartner.partnerNumber
        )
        if (partnerStudios.length > 0) {
          const teacherOnly = partnerStudios.every(
            s => s.role === 'teacher' || s.role === 'senior_teacher' || s.access_kind === 'senior_teacher'
          )
          if (teacherOnly) {
            navigate(`/partners/${currentPartner.partnerNumber}/studios/${partnerStudios[0].studio_internal_id}/teachers`)
            return
          }
          navigate(`/partners/${currentPartner.partnerNumber}/studios/${partnerStudios[0].studio_internal_id}/metrics`)
          return
        }
      }
      // Owner: сводная аналитика
      navigate(`/partners/${currentPartner.partnerNumber}/analytics`)
      return
    }
    navigate('/partners')
  }

  // Обработчик для активации скрытой кнопки очистки кэша
  const handleTitleClick = () => {
    clickCountRef.current += 1

    // Очищаем предыдущий таймаут
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current)
    }

    // Если тройной клик - показываем кнопку
    if (clickCountRef.current >= 3) {
      setShowClearCache(true)
      clickCountRef.current = 0

      // Скрываем кнопку через 5 секунд
      setTimeout(() => {
        setShowClearCache(false)
      }, 5000)
    } else {
      // Сбрасываем счетчик через 1 секунду
      clickTimeoutRef.current = setTimeout(() => {
        clickCountRef.current = 0
      }, 1000)
    }
  }

  const handleClearCache = async () => {
    if (isRefreshingApp) return
    if (confirm('Обновить приложение? Кэш будет очищен, вы останетесь в системе.')) {
      setIsRefreshingApp(true)
      try {
        await softRefresh()
      } catch (error) {
        console.error('❌ Header: failed to refresh app:', error)
        setIsRefreshingApp(false)
      }
    }
  }

  const handleHardReset = async () => {
    if (isRefreshingApp) return
    if (confirm('Полный сброс: очистить токен и кэш? Потребуется повторный вход.')) {
      setIsRefreshingApp(true)
      try {
        await hardReset()
      } catch (error) {
        console.error('❌ Header: failed to hard reset:', error)
        setIsRefreshingApp(false)
      }
    }
  }

  const userRole = getUserRole()
  const displayedStudios = getPartnerStudios(selectedPartnerNumber)
  const isPartnerContext = location.pathname.startsWith('/partners/')
  const isPartnerAnalyticsPage = /^\/partners\/[^/]+\/analytics$/.test(location.pathname)
  const isCityAnalyticsPage = /^\/partners\/[^/]+\/cities\/\d+\/analytics$/.test(location.pathname)
  const isStudioAnalyticsPage = /^\/partners\/[^/]+\/studios\/[^/]+\/analytics$/.test(location.pathname)
  const isStudioClientsPage = /^\/partners\/[^/]+\/studios\/[^/]+\/clients$/.test(location.pathname)
  const isStudioRatingsPage = /^\/partners\/[^/]+\/studios\/[^/]+\/ratings$/.test(location.pathname)
  const partnerOptions = useMemo(() => {
    const visibleStatuses = new Set(['OPEN', 'SUSPENDED', 'SERVICE'])
    const uniquePartnerNumbers = Array.from(new Set(
      studios
        .filter((studio) => visibleStatuses.has(studio.status_code || 'OPEN'))
        .map((studio) => studio.partner_number)
        .filter((value): value is number => value !== null && value !== undefined)
        .map((value) => Number(value))
        .filter((value) => !Number.isNaN(value))
    ))

    const partnerMap = new Map<number, { personsFio: string; cityList: string[] }>()
    for (const partner of partners) {
      partnerMap.set(partner.partner_number, {
        personsFio: partner.persons_fio || '',
        cityList: partner.city_list || []
      })
    }

    return uniquePartnerNumbers
      .map((partnerNumber) => {
        const fromStore = partnerMap.get(partnerNumber)
        const fallbackCities = Array.from(new Set(
          studios
            .filter((studio) =>
              Number(studio.partner_number) === partnerNumber &&
              visibleStatuses.has(studio.status_code || 'OPEN')
            )
            .map((studio) => studio.city?.city_name)
            .filter((city): city is string => Boolean(city))
        ))
        const cityList = fromStore?.cityList?.length ? [...fromStore.cityList] : fallbackCities
        const sortedCities = cityList.sort((a, b) => a.localeCompare(b, 'ru', { sensitivity: 'base' }))

        return {
          partnerNumber,
          primaryCity: sortedCities[0] || 'Город не указан',
          personsFio: fromStore?.personsFio || '',
          cityList: sortedCities
        }
      })
      .sort((a, b) => {
        const byCity = a.primaryCity.localeCompare(b.primaryCity, 'ru', { sensitivity: 'base' })
        if (byCity !== 0) return byCity
        return a.partnerNumber - b.partnerNumber
      })
  }, [studios, partners])
  const currentPartner = useMemo(() => {
    const fallback = partnerOptions[0] || null
    if (!fallback) return null
    if (selectedPartnerNumber === null) return fallback
    return partnerOptions.find((option) => option.partnerNumber === selectedPartnerNumber) || fallback
  }, [partnerOptions, selectedPartnerNumber])
  const teacherRoutePartnerNumber = Number(location.pathname.match(/^\/partners\/([^/]+)\//)?.[1])
  const teacherScopedStudios = useMemo(
    () => studios.filter(studio => Number(studio.partner_number) === teacherRoutePartnerNumber),
    [studios, teacherRoutePartnerNumber],
  )
  const teacherCities = useMemo(() => {
    const map = new Map<number, { cityId: number; cityName: string; studioId: number }>()
    teacherScopedStudios.forEach(studio => {
      if (!map.has(studio.city_id)) map.set(studio.city_id, {
        cityId: studio.city_id,
        cityName: studio.city?.city_name || 'Город',
        studioId: studio.studio_internal_id,
      })
    })
    return [...map.values()].sort((a, b) => a.cityName.localeCompare(b.cityName, 'ru'))
  }, [teacherScopedStudios])
  const currentTeacherCity = teacherScopedStudios.find(studio => studio.studio_internal_id === Number(location.pathname.match(/\/studios\/(\d+)/)?.[1]))?.city

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (partnerDropdownRef.current && !partnerDropdownRef.current.contains(event.target as Node)) {
        setIsPartnerDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (!isStudioRatingsPage) {
      setIsRatingsContextOpen(false)
      setIsPartnerDropdownOpen(false)
    }
  }, [isStudioRatingsPage])

  useEffect(() => {
    setIsTeacherCityOpen(false)
  }, [location.pathname])

  useEffect(() => {
    const handleOutsideCityClick = (event: MouseEvent) => {
      if (!(event.target instanceof Element) || !event.target.closest('[data-teacher-city-selector]')) {
        setIsTeacherCityOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutsideCityClick)
    return () => document.removeEventListener('mousedown', handleOutsideCityClick)
  }, [])

  const handlePartnerChange = (nextPartnerNumber: number) => {
    if (!confirmLeaveUnsavedChanges()) return
    const nextPartnerStudios = studios.filter((studio) => Number(studio.partner_number) === nextPartnerNumber)
    const nextIsSeniorOnly = nextPartnerStudios.length > 0 && nextPartnerStudios.every(
      (studio) => studio.access_kind === 'senior_teacher' || studio.role === 'senior_teacher'
    )
    if (nextIsSeniorOnly) {
      navigate(`/partners/${nextPartnerNumber}/studios/${nextPartnerStudios[0].studio_internal_id}/teachers`)
      return
    }

    if (isPartnerTeachersRoute && nextPartnerStudios[0]) {
      navigate(`/partners/${nextPartnerNumber}/studios/${nextPartnerStudios[0].studio_internal_id}/teachers${location.search.includes('view=analytics') ? '?view=analytics' : ''}`)
      return
    }

    const currentMatch = location.pathname.match(/^\/partners\/([^/]+)\/(.*)$/)
    const tail = currentMatch?.[2] || 'analytics'

    if (tail.startsWith('studios/')) {
      navigate(`/partners/${nextPartnerNumber}/analytics`)
      return
    }

    if (tail === 'studios' || tail === 'analytics') {
      navigate(`/partners/${nextPartnerNumber}/${tail}`)
      return
    }

    navigate(`/partners/${nextPartnerNumber}/analytics`)
  }

  const showSettingsButton =
    !isSeniorTeacherContext &&
    !isTeacherContext &&
    location.pathname !== '/settings/notifications' &&
    location.pathname !== '/management/settings' &&
    !/^\/partners\/[^/]+\/management$/.test(location.pathname)
  const isPartnerAccessibleForUk =
    /^\/partners\/[^/]+\/analytics$/.test(location.pathname) ||
    /^\/partners\/[^/]+\/management$/.test(location.pathname) ||
    /^\/partners\/[^/]+\/studios\/[^/]+\/(metrics|history|finance|analytics|clients|ratings|trainer-reviews|teachers(?:\/.*)?|management|studio-metrics|studio-metrics-grid|plan-history)$/.test(location.pathname)
  const managementPartnerFromPath = location.pathname.match(/^\/partners\/([^/]+)\//)?.[1] || null
  const managementPartnerFromStore = selectedPartnerNumber !== null
    ? String(selectedPartnerNumber)
    : (studios.find((studio) => studio.partner_number !== null && studio.partner_number !== undefined)?.partner_number != null
      ? String(studios.find((studio) => studio.partner_number !== null && studio.partner_number !== undefined)?.partner_number)
      : null)
  const partnerManagementPath = managementPartnerFromPath || managementPartnerFromStore
    ? `/partners/${managementPartnerFromPath || managementPartnerFromStore}/management`
    : '/partners'
  const settingsTargetPath = isManagementCompany ? '/management/settings' : partnerManagementPath

  const headerRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const el = headerRef.current
    if (!el) return
    const update = () =>
      document.documentElement.style.setProperty('--header-height', `${el.getBoundingClientRect().height}px`)
    update()
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <header ref={headerRef} className="tg-header sticky top-0 z-50 bg-card border-b border-border px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {shouldShowHomeButton() && (
            <button
              onClick={handleHomeClick}
              className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 hover:bg-primary/20 transition-colors"
              title="На главную"
            >
              <Home className="w-4 h-4 text-primary" />
            </button>
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <h1
                className="text-lg font-semibold text-foreground cursor-pointer select-none truncate"
                onClick={handleTitleClick}
                title="Тройной клик для открытия панели разработчика"
              >
                {getPageTitle()}
              </h1>
              {isStudioRatingsPage ? (
                <button
                  type="button"
                  onClick={() => setIsRatingsContextOpen((prev) => !prev)}
                  className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
                  aria-label={isRatingsContextOpen ? 'Скрыть селекторы' : 'Показать селекторы'}
                >
                  <ChevronDown className={`h-4 w-4 transition-transform ${isRatingsContextOpen ? 'rotate-180' : ''}`} />
                </button>
              ) : null}
            </div>
            {getPageSubtitle() && (
              <p className="mt-0.5 text-sm text-muted-foreground truncate">
                {getPageSubtitle()}
              </p>
            )}
            {isPartnerContext && partnerOptions.length > 1 && (!isStudioRatingsPage || isRatingsContextOpen) && (
              <div className="mt-1 relative min-w-0" ref={partnerDropdownRef}>
                <button
                  onClick={() => {
                    setIsTeacherCityOpen(false)
                    setIsPartnerDropdownOpen((prev) => {
                      if (prev) setPartnerSearch('')
                      return !prev
                    })
                  }}
                  className={`w-full min-w-0 text-left text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 group ${isStudioRatingsPage ? 'text-xs' : 'text-sm'}`}
                >
                  <span className="block text-left truncate">
                    {currentPartner?.personsFio || `Франчайзи №${currentPartner?.partnerNumber}`}
                  </span>
                  <ChevronDown
                    className={`w-3.5 h-3.5 shrink-0 transition-transform group-hover:text-foreground ${isPartnerDropdownOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {isPartnerDropdownOpen && (
                  <div className="absolute left-0 top-full mt-2 w-auto min-w-[300px] bg-card border border-border rounded-lg shadow-lg overflow-hidden z-50">
                    <div className="px-2 py-2 border-b border-border">
                      <div className="flex items-center gap-2 bg-muted/50 rounded-md px-2 py-1">
                        <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        <input
                          type="text"
                          value={partnerSearch}
                          onChange={(e) => setPartnerSearch(e.target.value)}
                          placeholder="Поиск по ФИО или городу..."
                          className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/60"
                          autoFocus
                        />
                      </div>
                    </div>
                    <div className="max-h-[320px] overflow-y-auto">
                      {partnerOptions
                        .filter((option) => {
                          if (!partnerSearch.trim()) return true
                          const q = partnerSearch.toLowerCase()
                          const fioMatch = option.personsFio?.toLowerCase().includes(q)
                          const cityMatch = option.cityList?.some((c) => c.toLowerCase().includes(q))
                          return fioMatch || cityMatch
                        })
                        .map((option) => {
                          const isSelected = option.partnerNumber === currentPartner?.partnerNumber
                          return (
                            <button
                              key={option.partnerNumber}
                              onClick={() => {
                                handlePartnerChange(option.partnerNumber)
                                setIsPartnerDropdownOpen(false)
                                setPartnerSearch('')
                              }}
                              className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/50 transition-colors text-left ${isSelected ? 'bg-primary/10' : ''}`}
                            >
                              <Building2 className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                              <div className="flex-1 min-w-0">
                                <div className={`text-sm font-medium truncate ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                                  {option.primaryCity}
                                </div>
                                <div className="text-xs text-muted-foreground truncate">
                                  {option.personsFio || `Франчайзи №${option.partnerNumber}`}
                                </div>
                              </div>
                              {isSelected && (
                                <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                              )}
                            </button>
                          )
                        })}
                    </div>
                  </div>
                )}
              </div>
            )}
            {isPartnerTeachersRoute && teacherCities.length > 0 && (
              <div className="relative mt-0.5 min-w-0" data-teacher-city-selector>
                <button
                  type="button"
                  onClick={() => setIsTeacherCityOpen(value => !value)}
                  className="flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  <span>{currentTeacherCity?.city_name || teacherCities[0].cityName}</span>
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isTeacherCityOpen ? 'rotate-180' : ''}`} />
                </button>
                {isTeacherCityOpen && (
                  <div className="absolute left-0 top-full z-50 mt-2 min-w-[260px] overflow-hidden rounded-lg border border-border bg-card shadow-lg">
                    {teacherCities.map(city => {
                      const isSelected = currentTeacherCity?.city_id === city.cityId
                      return <button key={city.cityId} type="button" onClick={() => {
                        navigate(`/partners/${teacherRoutePartnerNumber}/studios/${city.studioId}/teachers${location.search.includes('view=analytics') ? '?view=analytics' : ''}`)
                        setIsTeacherCityOpen(false)
                      }} className={`flex w-full items-center gap-2 px-3 py-2 text-left transition-colors ${isSelected ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50'}`}>
                        <MapPin className="h-4 w-4 shrink-0" />
                        <span className="text-sm font-medium">{city.cityName}</span>
                        {isSelected && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-primary" />}
                      </button>
                    })}
                  </div>
                )}
              </div>
            )}
            {isStudioContext && selectedStudio && !isPartnerTeachersRoute && !isStudioAnalyticsPage && !isStudioClientsPage && (!isStudioRatingsPage || isRatingsContextOpen) && (
              <HeaderStudioSelector
                studios={displayedStudios}
                currentStudioId={selectedStudio.studio_internal_id}
                compact={isStudioRatingsPage}
              />
            )}
            {planRouteType && !selectedStudio && (
              <p className="text-sm text-muted-foreground truncate">{matchedStudioLabel}</p>
            )}
            {(location.pathname === '/analytics' || location.pathname === '/management-analytics' || isPartnerAnalyticsPage || isCityAnalyticsPage || isStudioAnalyticsPage || isStudioClientsPage) && displayedStudios.length > 0 && (
              <GlobalAnalyticsSelector currentStudioId={(isStudioAnalyticsPage || isStudioClientsPage) ? selectedStudio?.studio_internal_id : undefined} />
            )}
          </div>
        </div>

        {user && (
          <div className="flex items-center gap-2 shrink-0">
            {isManagementCompany && isPartnerAccessibleForUk && (
              <span
                className="inline-block h-2 w-2 rounded-full bg-emerald-500/90"
                title="Этот экран доступен франчайзи"
                aria-label="Экран доступен франчайзи"
              />
            )}
            {isManagementCompany && <OriginalCaseNotificationsBell />}
            <ThemeToggle />
            {/* Скрытые кнопки разработчика */}
            {showClearCache && (
              <>
                {/* Кнопка обратной связи */}
                <FeedbackButton className="w-8 h-8 animate-pulse" />

                {/* Кнопка очистки кэша (сохраняет токен) */}
                <button
                  onClick={handleClearCache}
                  disabled={isRefreshingApp}
                  className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${isRefreshingApp ? 'bg-red-500/20 cursor-wait' : 'bg-red-500/10 hover:bg-red-500/20 animate-pulse'}`}
                  title={isRefreshingApp ? 'Обновляем...' : 'Обновить приложение'}
                >
                  <RotateCcw className="w-4 h-4 text-red-500" />
                </button>

                {/* Кнопка полного сброса (стирает токен) */}
                <button
                  onClick={handleHardReset}
                  disabled={isRefreshingApp}
                  className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${isRefreshingApp ? 'bg-red-500/20 cursor-wait' : 'bg-red-500/10 hover:bg-red-500/20 animate-pulse'}`}
                  title={isRefreshingApp ? 'Сброс...' : 'Полный сброс (выход)'}
                >
                  <LogOut className="w-4 h-4 text-red-500" />
                </button>
              </>
            )}
            {showSettingsButton && (
              <button
                onClick={() => {
                  if (!confirmLeaveUnsavedChanges()) return
                  navigate(settingsTargetPath)
                }}
                className="flex items-center justify-center w-8 h-8 rounded-lg bg-muted/60 hover:bg-muted transition-colors"
                title="Управление"
              >
                <Settings className="w-4 h-4 text-muted-foreground" />
              </button>
            )}
            {userRole && getRoleBadge(userRole)}
            <Avatar
              initials={getInitials(user.first_name, user.last_name)}
              size="sm"
            />
          </div>
        )}
      </div>
    </header>
  )
}
